import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import type { RootState } from '@/store';
import {
  getDashboardMetric16,
  type DashboardMetric16CycleFinalTestingBreakdown,
} from '@/services/dashboardService';
import { CompletedStatus } from '@/pages/exhibits/ex5/ControlsFilterDrawer';

const { Text } = Typography;

const COLORS = {
  notStarted: '#D1D5DB', // grey
  wip: '#FACC15', // yellow
  completed: '#22C55E', // green
};

type Row = {
  cycleId: string;
  cycleLabel: string; // "A00 Revenue (N=45)"
  total: number;

  notStartedCount: number;
  wipCount: number;
  completedCount: number;

  notStartedPct: number;
  wipPct: number;
  completedPct: number;
};

const safePercent = (part: number, total: number) =>
  total > 0 ? (part / total) * 100 : 0;

const pctText = (v: number) => `${Math.round(v)}%`;

const Metric16Tooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const row: Row | undefined = payload?.[0]?.payload;
  if (!row) return null;

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 8,
        padding: 10,
        minWidth: 260,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{row.cycleLabel}</div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          rowGap: 6,
          columnGap: 12,
        }}
      >
        <span>Not Started</span>
        <span style={{ fontWeight: 600 }}>
          {row.notStartedCount} ({pctText(row.notStartedPct)})
        </span>

        <span>WIP</span>
        <span style={{ fontWeight: 600 }}>
          {row.wipCount} ({pctText(row.wipPct)})
        </span>

        <span>Completed</span>
        <span style={{ fontWeight: 600 }}>
          {row.completedCount} ({pctText(row.completedPct)})
        </span>
      </div>

      <div style={{ marginTop: 8, color: 'rgba(0,0,0,0.6)' }}>
        Click a segment to open Exhibit 5
      </div>
    </div>
  );
};

export type EX1FinalTestingCompletionStatusByCycleStackedChartProps = {
  height?: number;
  title?: string;
};

export const EX1FinalTestingCompletionStatusByCycleStackedChart: React.FC<
  EX1FinalTestingCompletionStatusByCycleStackedChartProps
> = ({ height = 380, title = 'Final completion status' }) => {
  const navigate = useNavigate();

  const { selectedReport } = useSelector(
    (state: RootState) => state.annualReport,
  );
  const annualReportId = selectedReport?.annualReportId;

  const [raw, setRaw] = useState<DashboardMetric16CycleFinalTestingBreakdown[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!annualReportId) {
        setRaw([]);
        setLoading(false);
        setErrorMessage(null);
        return;
      }

      setLoading(true);
      setErrorMessage(null);

      try {
        const data = await getDashboardMetric16(annualReportId);
        if (!isMounted) return;
        setRaw(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!isMounted) return;
        setRaw([]);
        setErrorMessage(
          err?.message ||
            err?.Message ||
            'Failed to load Metric 16 (Final testing completion breakdown by cycle).',
        );
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [annualReportId]);

  const data: Row[] = useMemo(() => {
    return raw
      .map(x => {
        const totalFromApi = x.controlCount ?? 0;
        const ns = x.notStartedCount ?? 0;
        const wip = x.wipCount ?? 0;
        const comp = x.completedCount ?? 0;

        const denom = totalFromApi > 0 ? totalFromApi : ns + wip + comp;

        return {
          cycleId: x.cycleId,
          cycleLabel: `${x.cycleCode} ${x.cycleName} (Count: ${denom})`,
          total: denom,

          notStartedCount: ns,
          wipCount: wip,
          completedCount: comp,

          notStartedPct: safePercent(ns, denom),
          wipPct: safePercent(wip, denom),
          completedPct: safePercent(comp, denom),
        };
      })
      //.sort((a, b) => b.total - a.total);
  }, [raw]);

  const isEmpty = !loading && !errorMessage && data.length === 0;

  const goToExhibit5 = (cycleId: string, cycleLabel: string, segment: CompletedStatus) => {
    const cycleCode = cycleLabel.split(' ')[0];
    const cycleName = cycleLabel.split(' ')[1];
    navigate('/control-activities', {
      state: { 
        cycleId: cycleId,
        cycleCode: cycleCode ?? '',
        cycleName: cycleName ?? '',
        finalStatus: segment,
        testingScope: true
       },
    });
  };

  // Label must show: "20 (45%)" inside each segment.
  // Hide labels if segment is too small to fit.
  const PercentLabel = (props: any) => {
    const { x, y, width, height, value } = props;
    const v = Number(value ?? 0);
    if (!Number.isFinite(v) || v < 8) return null; // same as metric 15
    const cx = x + width / 2;
    const cy = y + height / 2;

    return (
      <text
        x={cx}
        y={cy}
        textAnchor='middle'
        dominantBaseline='middle'
        fontSize={12}
        fontWeight={600}
      >
        {pctText(v)}
      </text>
    );
  };

  const RightCountTick = (props: any): React.ReactElement => {
    const { x, y, payload } = props;
    const row: Row | undefined = payload?.payload;
    if (!row) return <text />;

    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor='start'
        fontSize={12}
        fill='rgba(0,0,0,0.65)'
      >
        n = {row.total}
      </text>
    );
  };

  const legendFormatter = (value: any) => {
    if (value === 'notStartedPct') return 'Not Started';
    if (value === 'wipPct') return 'WIP';
    if (value === 'completedPct') return 'Completed';
    return value;
  };

  return (
    <Card title={title}>
      <div style={{ height }}>
        {loading ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin />
          </div>
        ) : errorMessage ? (
          <Alert
            type='error'
            message='Dashboard Metric 16 Error'
            description={errorMessage}
            showIcon
          />
        ) : isEmpty ? (
          <Alert
            type='info'
            message='No data'
            description='No active in-scope controls were returned for final testing completion.'
            showIcon
          />
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart
              data={data}
              layout='vertical'
              barCategoryGap={10}
              margin={
                isNarrow
                  ? { top: 8, right: 10, bottom: 40, left: 10 }
                  : { top: 8, right: 60, bottom: 8, left: 10 }
              }
            >
              <Tooltip content={<Metric16Tooltip />} />

              <Legend
                layout={isNarrow ? 'horizontal' : 'vertical'}
                align={isNarrow ? 'center' : 'right'}
                verticalAlign={isNarrow ? 'bottom' : 'middle'}
                wrapperStyle={
                  isNarrow ? { paddingTop: 12 } : { paddingLeft: 16 }
                }
                formatter={legendFormatter}
              />

              <XAxis
                type='number'
                domain={[0, 100]}
                tickFormatter={v => `${v}%`}
                allowDecimals={false}
              />

              <YAxis
                type='category'
                dataKey='cycleLabel'
                width={isNarrow ? 110 : 190}
                tick={{ fontSize: 12 }}
              />

              <YAxis
                yAxisId='right'
                orientation='right'
                type='category'
                dataKey='cycleLabel'
                axisLine={false}
                tickLine={false}
                width={isNarrow ? 40 : 55}
                tick={RightCountTick}
              />

              <Bar
                dataKey='notStartedPct'
                stackId='a'
                fill={COLORS.notStarted}
                isAnimationActive={false}
                onClick={(_, idx) =>
                  data[idx]?.cycleId &&
                  goToExhibit5(data[idx].cycleId, data[idx].cycleLabel, 'NotStarted')
                }
                style={{ cursor: 'pointer' }}
              >
                <LabelList dataKey="notStartedPct" content={PercentLabel} />
              </Bar>

              <Bar
                dataKey='wipPct'
                stackId='a'
                fill={COLORS.wip}
                isAnimationActive={false}
                onClick={(_, idx) =>
                  data[idx]?.cycleId && goToExhibit5(data[idx].cycleId, data[idx].cycleLabel, 'WIP')
                }
                style={{ cursor: 'pointer' }}
              >
                <LabelList dataKey="wipPct" content={PercentLabel} />
              </Bar>

              <Bar
                dataKey='completedPct'
                stackId='a'
                fill={COLORS.completed}
                isAnimationActive={false}
                onClick={(_, idx) =>
                  data[idx]?.cycleId &&
                  goToExhibit5(data[idx].cycleId, data[idx].cycleLabel, 'Completed')
                }
                style={{ cursor: 'pointer' }}
              >
                <LabelList dataKey="completedPct" content={PercentLabel} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {!loading && !errorMessage ? (
        <div style={{ marginTop: 8, textAlign: 'center' }}>
          <Text type='secondary'>
            Active, in-scope controls grouped by cycle.
          </Text>
        </div>
      ) : null}
    </Card>
  );
};

export default EX1FinalTestingCompletionStatusByCycleStackedChart;
